import { useCallback, useMemo } from "react";
import { useAuthStore } from "@/store/auth";

/**
 * A hook to check user permissions throughout the application.
 * It provides a simple `hasPermission` function that abstracts the logic
 * of checking the user's permission list and handling super-admin cases ('*').
 *
 * @returns An object containing the `hasPermission` function and the raw `permissions` list.
 *
 * @example
 * const { hasPermission } = usePermissions();
 * // In a component:
 * {hasPermission(PERMISSIONS.ACCOUNTS_SUSPEND) && (
 *   <Button>Suspend</Button>
 * )}
 */
export const usePermissions = () => {
  const user = useAuthStore((state) => state.user);
  const permissions = useMemo(() => user?.permissions || [], [user]);

  // A super-admin permission ('*') grants all other permissions.
  const isSuperAdmin = permissions.includes("*");

  /**
   * Checks if the current user has a specific permission.
   * @param requiredPermission The permission string to check for (e.g., 'users:read'), imported from `lib/permissions.ts`.
   * @returns `true` if the user has the permission, otherwise `false`.
   */
  const hasPermission = useCallback(
    (requiredPermission: string): boolean => {
      if (!requiredPermission) {
        console.warn(
          "usePermissions: hasPermission was called with an undefined or empty requiredPermission."
        );
        return false;
      }

      if (isSuperAdmin) {
        return true;
      }

      return permissions.includes(requiredPermission);
    },
    [isSuperAdmin, permissions]
  );

  return {
    hasPermission,
    permissions,
    isSuperAdmin,
  };
};
